import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Üst düzey hata yakalayıcı.
 * React'in render sırasında attığı hataları yakalar,
 * uygulamanın "beyaz ekran" göstermesini engeller.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-[#0a0a0b]">
          <div className="max-w-md w-full dribbble-panel p-8 text-center">
            {/* Animasyonlu uyarı ikonu */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <span className="absolute inset-0 bg-red-100 rounded-3xl animate-ping opacity-60" />
              <div className="relative w-20 h-20 bg-red-500 rounded-3xl flex items-center justify-center shadow-lg shadow-red-300/50">
                <AlertTriangle className="w-10 h-10 text-white" strokeWidth={2} />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Bir şeyler ters gitti
            </h1>
            <p className="text-sm text-gray-600 mb-6">
              Beklenmeyen bir hata oluştu. Sayfayı yenilemeyi deneyebilirsiniz.
            </p>

            {/* Hata detayı — sadece development modunda */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left mb-6 bg-gray-50 dark:bg-[#18181c] rounded-lg p-3 border border-gray-200">
                <summary className="text-xs font-semibold text-gray-700 cursor-pointer mb-2">
                  🔍 Teknik Detay
                </summary>
                <pre className="text-[10px] text-red-600 overflow-auto whitespace-pre-wrap break-words mt-2">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack?.split('\n').slice(0, 4).join('\n')}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="btn btn-secondary inline-flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Tekrar Dene
              </button>
              <button
                onClick={this.handleReload}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Sayfayı Yenile
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
